import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSiteConfig, setSiteConfig, getAdmins, addAdmin, removeAdmin } from '../services/firestoreService';
import { auth } from '../firebase';

const AdminSettingsPage = () => {
  const navigate = useNavigate();
  // Site config state
  const [siteConfig, setSiteConfigState] = useState({
    title: '',
    shortName: '',
    description: '',
    showVideosButton: true,
    uploadPin: '',
    uploadPinUpdatedAt: null
  });
  const [previousConfig, setPreviousConfig] = useState(null);
  const [showUploadPin, setShowUploadPin] = useState(false);

  // Admin management state
  const [admins, setAdmins] = useState([]);
  const [newAdminEmail, setNewAdminEmail] = useState('');

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [config, adminsList] = await Promise.all([
        getSiteConfig(),
        getAdmins()
      ]);

      const configState = {
        title: config.title || '',
        shortName: config.shortName || '',
        description: config.description || '',
        showVideosButton: config.showVideosButton !== undefined ? config.showVideosButton : true,
        uploadPin: config.uploadPin || '202526',
        uploadPinUpdatedAt: config.uploadPinUpdatedAt || null
      };

      setSiteConfigState(configState);
      setPreviousConfig(configState);
      setAdmins(adminsList);
    } catch (error) {
      console.error('Error loading admin settings:', error);
      showMessage('error', 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleSiteConfigChange = (field, value) => {
    setSiteConfigState(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSiteConfig = async () => {
    try {
      setSaving(true);
      await setSiteConfig(siteConfig, previousConfig);
      setPreviousConfig(siteConfig);
      showMessage('success', 'Site configuration saved successfully!');
    } catch (error) {
      console.error('Error saving site config:', error);
      showMessage('error', 'Failed to save site configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleAddAdmin = async () => {
    const email = newAdminEmail.trim().toLowerCase();

    if (!email) {
      showMessage('error', 'Please enter an email address');
      return;
    }

    // Basic email validation
    if (!email.includes('@') || !email.includes('.')) {
      showMessage('error', 'Please enter a valid email address');
      return;
    }

    if (admins.find(a => a.email === email)) {
      showMessage('error', 'This email is already an admin');
      return;
    }

    try {
      // Get current user's email to track who added this admin
      const currentUserEmail = auth.currentUser?.email || 'admin-settings-page';
      await addAdmin(email, currentUserEmail);

      // Reload admin list to get the new admin with full data
      const updatedAdmins = await getAdmins();
      setAdmins(updatedAdmins);
      setNewAdminEmail('');
      showMessage('success', `Added ${email} as admin`);
    } catch (error) {
      console.error('Error adding admin:', error);
      showMessage('error', 'Failed to add admin');
    }
  };

  const handleRemoveAdmin = async (adminObj) => {
    if (!window.confirm(`Remove ${adminObj.email} as admin?`)) {
      return;
    }

    try {
      await removeAdmin(adminObj.email);
      setAdmins(admins.filter(a => a.email !== adminObj.email));
      showMessage('success', `Removed ${adminObj.email} as admin`);
    } catch (error) {
      console.error('Error removing admin:', error);
      showMessage('error', 'Failed to remove admin');
    }
  };

  const canDeleteAdmin = (adminObj) => {
    // Superadmins (added by 'superadmin') cannot be deleted
    return adminObj.addedBy !== 'superadmin';
  };

  if (loading) {
    return (
      <div className="w-full p-8">
        <div className="text-center py-12 text-text-dark text-lg">Loading admin settings...</div>
      </div>
    );
  }

  return (
    <div className="w-full p-8">
      {/* Header */}
      <header className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/')}
          className="p-2 bg-transparent border-none text-text-dark cursor-pointer transition-colors duration-200 flex items-center gap-2 hover:bg-gray-100 rounded"
          title="Back to Home"
        >
          <span className="material-icons">arrow_back</span>
        </button>
        <h1 className="m-0 text-text-dark text-3xl font-light font-serif">Admin Settings</h1>
      </header>

      {/* Success/Error Message */}
      {message.text && (
        <div className={`mb-6 p-4 rounded ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {message.text}
        </div>
      )}

      {/* Site Configuration Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-2xl font-semibold text-text-dark font-serif mb-4">Site Configuration</h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-dark mb-2">
              Site Title
            </label>
            <input
              type="text"
              value={siteConfig.title}
              onChange={(e) => handleSiteConfigChange('title', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-accent"
              placeholder="CHS Volleyball"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-dark mb-2">
              Short Name
            </label>
            <input
              type="text"
              value={siteConfig.shortName}
              onChange={(e) => handleSiteConfigChange('shortName', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-accent"
              placeholder="CHS Volleyball"
            />
            <p className="text-xs text-gray-500 mt-1">Used in browser tabs and bookmarks</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-dark mb-2">
              Description
            </label>
            <textarea
              value={siteConfig.description}
              onChange={(e) => handleSiteConfigChange('description', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-accent"
              rows="3"
              placeholder="CHS Volleyball photo gallery and game videos"
            />
            <p className="text-xs text-gray-500 mt-1">Used in search engine results</p>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="showVideosButton"
              checked={siteConfig.showVideosButton}
              onChange={(e) => handleSiteConfigChange('showVideosButton', e.target.checked)}
              className="w-5 h-5 text-accent focus:ring-accent border-gray-300 rounded cursor-pointer"
            />
            <label htmlFor="showVideosButton" className="text-sm font-medium text-text-dark cursor-pointer">
              Show Videos button on home page
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-dark mb-2">
              Upload PIN
            </label>
            <div className="flex gap-2 items-center">
              <input
                type={showUploadPin ? "text" : "password"}
                value={siteConfig.uploadPin}
                onChange={(e) => handleSiteConfigChange('uploadPin', e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-accent font-mono"
                placeholder="202526"
                maxLength="6"
              />
              <button
                type="button"
                onClick={() => setShowUploadPin(!showUploadPin)}
                className="p-2 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 transition-colors"
                title={showUploadPin ? "Hide PIN" : "Show PIN"}
              >
                <span className="material-icons text-base">{showUploadPin ? 'visibility_off' : 'visibility'}</span>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              6-digit PIN for anonymous photo uploads
              {siteConfig.uploadPinUpdatedAt && (
                <span className="block mt-1">
                  Last updated: {new Date(siteConfig.uploadPinUpdatedAt).toLocaleString()}
                </span>
              )}
            </p>
          </div>

          <button
            onClick={handleSaveSiteConfig}
            disabled={saving}
            className="px-6 py-3 bg-accent text-text-light border-none rounded text-base cursor-pointer transition-colors duration-200 hover:bg-accent/80 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Site Configuration'}
          </button>
        </div>
      </div>

      {/* Admin Management Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-semibold text-text-dark font-serif mb-4">Administrator Emails</h2>

        {/* Current Admins List */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-text-dark mb-3">Current Administrators</h3>
          {admins.length === 0 ? (
            <p className="text-sm text-gray-500">No administrators found</p>
          ) : (
            <div className="space-y-2">
              {admins.map(adminObj => (
                <div key={adminObj.email} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div className="flex flex-col">
                    <span className="text-sm text-text-dark font-medium">{adminObj.email}</span>
                    {adminObj.addedBy && (
                      <span className="text-xs text-gray-500">
                        {adminObj.addedBy === 'superadmin'
                          ? 'Superadmin (cannot be removed)'
                          : `Added by: ${adminObj.addedBy}`}
                      </span>
                    )}
                  </div>
                  {canDeleteAdmin(adminObj) ? (
                    <button
                      onClick={() => handleRemoveAdmin(adminObj)}
                      className="p-1 bg-transparent border-none text-red-600 cursor-pointer transition-colors duration-200 hover:text-red-800"
                      title="Remove admin"
                    >
                      <span className="material-icons text-base">delete</span>
                    </button>
                  ) : (
                    <span className="material-icons text-base text-gray-400" title="Cannot remove superadmin">
                      lock
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add New Admin */}
        <div>
          <h3 className="text-sm font-medium text-text-dark mb-3">Add New Administrator</h3>
          <div className="flex gap-3">
            <input
              type="email"
              value={newAdminEmail}
              onChange={(e) => setNewAdminEmail(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddAdmin()}
              className="flex-1 px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-accent"
              placeholder="admin@example.com"
            />
            <button
              onClick={handleAddAdmin}
              className="px-6 py-2 bg-accent text-text-light border-none rounded text-base cursor-pointer transition-colors duration-200 hover:bg-accent/80"
            >
              Add Admin
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettingsPage;
